import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { tenantService } from "@/services/breeze/TenantService";
import {
  UpdateTenantRequest,
  TenantStorageUsage,
  TenantResourceUsage,
} from "@/types/breeze/Tenant";
import { useAuth } from "@/providers/AuthProvider";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import InputFileField from "@/components/form/InputFileField";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import PageTemplate from "@/components/page-template/PageTemplate";
import InputField from "@/components/form/InputField";
import CheckboxField from "@/components/form/CheckboxField";
import {
  Box,
  Card as RadixCard,
  Flex,
  Grid,
  Heading,
  Button,
  Text,
  Progress,
  Link,
} from "@radix-ui/themes";
import { ThemeSettings } from "@/components/ui/ThemeSettings";
import ArrayField from "@/components/form/ArrayField";
import ErrorCard from "@/components/ui/ErrorCard";
import { ExternalLink } from "lucide-react";

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const StorageUsageSection = ({
  storageUsage,
}: {
  storageUsage: TenantStorageUsage;
}) => {
  return (
    <Box>
      <Box mb="3">
        <Flex justify="between" mb="2">
          <Text as="div" size="2" color="gray">
            {formatBytes(storageUsage.total_size_bytes)} used of{" "}
            {storageUsage.quota_gigabytes}GB quota
          </Text>
          <Text
            as="div"
            size="2"
            color={storageUsage.usage_percentage >= 90 ? "red" : "gray"}
          >
            {storageUsage.usage_percentage.toFixed(1)}%
          </Text>
        </Flex>
        <Progress
          value={storageUsage.usage_percentage}
          style={
            {
              height: "8px",
              "--progress-indicator-color":
                storageUsage.usage_percentage >= 90
                  ? "var(--red-9)"
                  : undefined,
            } as React.CSSProperties
          }
        />
      </Box>

      <Text as="div" size="1" color="gray" mt="2">
        Last calculated:{" "}
        {new Date(storageUsage.last_calculated).toLocaleString()}
      </Text>
    </Box>
  );
};

const UserUsageSection = ({
  userUsage,
}: {
  userUsage: TenantResourceUsage;
}) => {
  return (
    <Box mb="3">
      <Text as="div" size="2" weight="medium" mb="2">
        Monthly Active Users
      </Text>
      <Box>
        <Flex justify="between" mb="2">
          <Text as="div" size="2" color="gray">
            {userUsage.usage} used of {userUsage.quota} monthly active users
          </Text>
          <Text
            as="div"
            size="2"
            color={userUsage.usage / userUsage.quota >= 90 ? "red" : "gray"}
          >
            {((userUsage.usage / userUsage.quota) * 100).toFixed(1)}%
          </Text>
        </Flex>
        <Progress
          value={(userUsage.usage / userUsage.quota) * 100}
          style={
            {
              height: "8px",
              "--progress-indicator-color":
                userUsage.usage / userUsage.quota >= 90
                  ? "var(--red-9)"
                  : undefined,
            } as React.CSSProperties
          }
        />
      </Box>

      <Text as="div" size="1" color="gray" mt="2">
        Last calculated: {new Date().toLocaleString()}
      </Text>
    </Box>
  );
};

const EmailUsageSection = ({
  emailUsage,
}: {
  emailUsage: TenantResourceUsage;
}) => {
  return (
    <Box mb="3">
      <Text as="div" size="2" weight="medium" mb="2">
        Emails Sent Today
      </Text>
      <Box>
        <Flex justify="between" mb="2">
          <Text as="div" size="2" color="gray">
            {emailUsage.usage} used of {emailUsage.quota} emails
          </Text>
          <Text
            as="div"
            size="2"
            color={emailUsage.usage / emailUsage.quota >= 90 ? "red" : "gray"}
          >
            {((emailUsage.usage / emailUsage.quota) * 100).toFixed(1)}%
          </Text>
        </Flex>
        <Progress
          value={(emailUsage.usage / emailUsage.quota) * 100}
          style={
            {
              height: "8px",
              "--progress-indicator-color":
                emailUsage.usage / emailUsage.quota >= 90
                  ? "var(--red-9)"
                  : undefined,
            } as React.CSSProperties
          }
        />
      </Box>

      <Text as="div" size="1" color="gray" mt="2">
        Resets at midnight daily.
      </Text>
    </Box>
  );
};

const WorkflowUsageSection = ({
  workflowUsage,
}: {
  workflowUsage: TenantResourceUsage;
}) => {
  return (
    <Box mb="3">
      <Text as="div" size="2" weight="medium" mb="2">
        Workflow Runs This Month
      </Text>
      <Box>
        <Flex justify="between" mb="2">
          <Text as="div" size="2" color="gray">
            {workflowUsage.usage} used of {workflowUsage.quota} workflow runs
          </Text>
          <Text
            as="div"
            size="2"
            color={
              workflowUsage.usage / workflowUsage.quota >= 90 ? "red" : "gray"
            }
          >
            {((workflowUsage.usage / workflowUsage.quota) * 100).toFixed(1)}%
          </Text>
        </Flex>
        <Progress
          value={(workflowUsage.usage / workflowUsage.quota) * 100}
          style={
            {
              height: "8px",
              "--progress-indicator-color":
                workflowUsage.usage / workflowUsage.quota >= 90
                  ? "var(--red-9)"
                  : undefined,
            } as React.CSSProperties
          }
        />
      </Box>
    </Box>
  );
};

const OrganisationSettingsPage = withTenantAdminRequired(
  function OrganisationSettingsPage() {
    const queryClient = useQueryClient();
    const { currentTenant } = useAuth();

    const {
      data: tenant,
      isLoading: isTenantLoading,
      error: tenantError,
    } = useQuery({
      queryKey: ["tenant", currentTenant?.id],
      queryFn: async () => {
        return tenantService.getTenantById(currentTenant!.id);
      },
      enabled: !!currentTenant?.id,
    });

    const {
      data: storageUsage,
      isLoading: isStorageLoading,
      error: storageError,
    } = useQuery({
      queryKey: ["tenant-storage", currentTenant?.id],
      queryFn: async () => {
        return tenantService.getTenantStorageUsage(currentTenant!.id);
      },
      enabled: !!currentTenant?.id,
      // Refresh every 5 minutes
      refetchInterval: 5 * 60 * 1000,
    });

    const { mutate: updateTenant, isPending: isUpdatingTenant } = useMutation({
      mutationFn: (data: UpdateTenantRequest) =>
        tenantService.updateTenant(currentTenant!.id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["tenant", currentTenant?.id],
        });
      },
    });

    if (isTenantLoading || isStorageLoading) return <LoadingScreen />;
    if (tenantError || storageError)
      return <ErrorCard error="Error loading organisation settings" />;

    if (!tenant) {
      return (
        <Box p="4">
          <Text size="2" color="gray">
            No organisation settings found
          </Text>
        </Box>
      );
    }

    return (
      <PageTemplate
        title="Organisation Settings"
        rightContent={
          <Button
            type="submit"
            form="org-settings-form"
            loading={isUpdatingTenant}
          >
            Save Changes
          </Button>
        }
      >
        <Formik<UpdateTenantRequest>
          initialValues={{
            name: tenant.name,
            description: tenant.description,
            google_maps_key: tenant.google_maps_key,
            logo_square_id: tenant.logo_square?.id,
            logo_standard_id: tenant.logo_standard?.id,
            tenant_settings: {
              allow_registrations:
                tenant.tenant_settings?.allow_registrations || false,
              default_role_id: tenant.tenant_settings?.default_role_id || 0,
              app_engagement_trial_enabled:
                tenant.tenant_settings?.app_engagement_trial_enabled || false,
              app_engagement_trial_days:
                tenant.tenant_settings?.app_engagement_trial_days ?? 7,
              allowed_application_urls:
                tenant.tenant_settings?.allowed_application_urls || [],
              payment_success_url:
                tenant.tenant_settings?.payment_success_url || "",
              payment_cancel_url:
                tenant.tenant_settings?.payment_cancel_url || "",
              enable_group_rls:
                tenant.tenant_settings?.enable_group_rls || false,
            },
            theme_settings: {
              primary_color: tenant.theme_settings?.primary_color || "orange",
              gray_color: tenant.theme_settings?.gray_color || "gray",
              radius: tenant.theme_settings?.radius,
            },
            require_email_confirmation: tenant.require_email_confirmation,
          }}
          onSubmit={(values) => {
            updateTenant(values as UpdateTenantRequest);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form id="org-settings-form">
              <Flex direction="column" gap="6">
                {/* Two-column grid for settings cards */}
                <Grid columns={{ initial: "1", md: "2" }} gap="6">
                  {/* Left column */}
                  <Flex direction="column" gap="6">
                    {/* General Settings */}
                    <RadixCard size="3">
                      <Box p="1">
                        <Heading as="h2" size="4" weight="medium" mb="4">
                          General Settings
                        </Heading>
                        <Flex direction="column" gap="4">
                          <InputField
                            name="name"
                            label="Name"
                            required
                            options={{
                              placeholder: "Enter your organisation name",
                            }}
                          />
                          <InputField
                            name="description"
                            label="Description"
                            options={{
                              placeholder:
                                "Enter your organisation description",
                            }}
                          />

                          <InputField
                            name="google_maps_key"
                            label="Google Maps Key"
                            options={{
                              placeholder: "Enter your Google Maps key",
                            }}
                            description="Enter your Google Maps key to use Google Maps in your application. You can get your key from the Google Cloud Console."
                          />

                          <Text size="2" color="gray">
                            New user registration, default role, email
                            confirmation and the optional engagement trial now
                            live with the rest of the user-lifecycle controls on
                            the{" "}
                            <a
                              href="/settings/users"
                              style={{ color: "var(--accent-11)" }}
                            >
                              Users
                            </a>{" "}
                            page.
                          </Text>
                        </Flex>
                      </Box>
                    </RadixCard>

                    {/* Application URLs Card */}
                    <RadixCard size="3">
                      <Box p="1">
                        <Heading as="h2" size="4" weight="medium" mb="4">
                          Allowed Application URLs
                        </Heading>
                        <ArrayField
                          name="tenant_settings.allowed_application_urls"
                          placeholder="https://example.com or *.example.com"
                          description="Configure which URLs can access your API. Leave empty to use the default CORS policy (Anythink domains and localhost). Supports wildcard subdomains (e.g., *.example.com). It may take up to 2 minutes for changes to take effect."
                          emptyStateText="No URLs configured. The default CORS policy will be used, allowing access from Anythink domains and localhost."
                          itemLabel="URL"
                          validation={(url) => {
                            // Basic URL validation - allow wildcards and protocols
                            if (url.startsWith("*.")) return true;
                            try {
                              new URL(
                                url.startsWith("http") ? url : `https://${url}`,
                              );
                              return true;
                            } catch {
                              return false;
                            }
                          }}
                        />
                      </Box>
                    </RadixCard>

                    {/* Payment Settings Card */}
                    <RadixCard size="3">
                      <Box p="1">
                        <Heading as="h2" size="4" weight="medium" mb="4">
                          Payment Settings
                        </Heading>
                        <Flex direction="column" gap="4">
                          <Box>
                            <InputField
                              name="tenant_settings.payment_success_url"
                              label="Payment Success URL"
                              options={{
                                placeholder: `https://example.com/payment/success`,
                              }}
                              description="Custom redirect URL when a payment is completed successfully. Leave empty to use the default Anythink success page."
                            />
                            <Flex mt="2" align="center" gap="2">
                              <Text size="2" color="gray">
                                Preview default page:
                              </Text>
                              <Link
                                href={`/org/${currentTenant?.id}/integrations/anythinkpay/payments/success?payment_id=preview_12345`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="2"
                              >
                                <Flex align="center" gap="1">
                                  Success Page
                                  <ExternalLink size={12} />
                                </Flex>
                              </Link>
                            </Flex>
                          </Box>

                          <Box>
                            <InputField
                              name="tenant_settings.payment_cancel_url"
                              label="Payment Cancel URL"
                              options={{
                                placeholder: `https://example.com/payment/cancel`,
                              }}
                              description="Custom redirect URL when a payment is cancelled. Leave empty to use the default Anythink cancellation page."
                            />
                            <Flex mt="2" align="center" gap="2">
                              <Text size="2" color="gray">
                                Preview default page:
                              </Text>
                              <Link
                                href={`/org/${currentTenant?.id}/integrations/anythinkpay/payments/cancelled?session_id=preview_session_67890`}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="2"
                              >
                                <Flex align="center" gap="1">
                                  Cancellation Page
                                  <ExternalLink size={12} />
                                </Flex>
                              </Link>
                            </Flex>
                          </Box>
                        </Flex>
                      </Box>
                    </RadixCard>

                    <RadixCard size="3">
                      <Box p="1">
                        <Heading as="h2" size="4" weight="medium" mb="4">
                          Access Controls
                        </Heading>
                        <CheckboxField
                          name="tenant_settings.enable_group_rls"
                          label="Enable group-level row access"
                          description="When on, records can be shared with a whole group. Members of the group inherit read (and optionally write) access to any row the group has been granted."
                        />
                      </Box>
                    </RadixCard>
                  </Flex>

                  {/* Right column */}
                  <Flex direction="column" gap="6">
                    {/* Branding Settings */}
                    <RadixCard size="3">
                      <Box p="1">
                        <Heading as="h2" size="4" weight="medium" mb="4">
                          Branding
                        </Heading>
                        <Flex direction="column" gap="6">
                          <Box>
                            <Text as="div" size="2" mb="2" weight="medium">
                              Logos
                            </Text>
                            <Grid columns="2" gap="4">
                              <InputFileField
                                name="logo_square_id"
                                label="Square Logo"
                                options={{
                                  accept: "image/*",
                                  max_size: 5, // 5MB
                                  max_files: 1,
                                }}
                                onChange={(fileId: any) => {
                                  setFieldValue("logo_square_id", fileId);
                                }}
                                restrictedType="image"
                              />

                              <InputFileField
                                name="logo_standard_id"
                                label="Standard Logo"
                                options={{
                                  accept: "image/*",
                                  max_size: 5, // 5MB
                                  max_files: 1,
                                }}
                                onChange={(fileId: any) => {
                                  setFieldValue("logo_standard_id", fileId);
                                }}
                                restrictedType="image"
                              />
                            </Grid>
                          </Box>

                          <Box>
                            <Text as="div" size="2" mb="2" weight="medium">
                              Theme
                            </Text>
                            <ThemeSettings
                              value={values.theme_settings}
                              onChange={(themeSettings) =>
                                setFieldValue("theme_settings", themeSettings)
                              }
                            />
                          </Box>
                        </Flex>
                      </Box>
                    </RadixCard>

                    {/* Storage Usage Card */}
                    <RadixCard size="3">
                      <Box p="1">
                        {storageUsage ? (
                          <Flex direction="column" gap="6">
                            <Grid columns={{ initial: "1", md: "2" }} gap="6">
                              <Box>
                                <Heading
                                  as="h2"
                                  size="4"
                                  weight="medium"
                                  mb="3"
                                >
                                  Database Quota
                                </Heading>
                                <StorageUsageSection
                                  storageUsage={storageUsage.storage_usage}
                                />
                              </Box>

                              <Box>
                                <Heading
                                  as="h2"
                                  size="4"
                                  weight="medium"
                                  mb="3"
                                >
                                  File Quota
                                </Heading>
                                <StorageUsageSection
                                  storageUsage={storageUsage.file_usage}
                                />
                              </Box>

                              <Box>
                                <Heading
                                  as="h2"
                                  size="4"
                                  weight="medium"
                                  mb="3"
                                >
                                  User Quota
                                </Heading>
                                <UserUsageSection
                                  userUsage={storageUsage.user_usage}
                                />
                              </Box>

                              <Box>
                                <Heading
                                  as="h2"
                                  size="4"
                                  weight="medium"
                                  mb="3"
                                >
                                  Email Quota
                                </Heading>
                                <EmailUsageSection
                                  emailUsage={storageUsage.email_usage}
                                />
                              </Box>

                              <Box>
                                <Heading
                                  as="h2"
                                  size="4"
                                  weight="medium"
                                  mb="3"
                                >
                                  Workflow Quota
                                </Heading>
                                <WorkflowUsageSection
                                  workflowUsage={storageUsage.workflow_usage}
                                />
                              </Box>
                            </Grid>

                            <Text color="gray" mt="1" size="2">
                              Need more? Please contact us to request a quota
                              increase or upgrade your plan via MyAnythink.
                            </Text>
                          </Flex>
                        ) : (
                          <Text as="div" size="2" color="gray">
                            Loading usage data...
                          </Text>
                        )}
                      </Box>
                    </RadixCard>
                  </Flex>
                </Grid>
              </Flex>
            </Form>
          )}
        </Formik>
      </PageTemplate>
    );
  },
);

export default OrganisationSettingsPage;
